import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';

// ============================================================================
// FIREBASE CONFIGURATION - Your credentials are already set!
// ============================================================================

const firebaseConfig = {
  apiKey: "AIzaSyCYXh57sqVoQrJqjQIi9QR6Hi5Ki3ikva4",
  authDomain: "surf-strong.firebaseapp.com",
  projectId: "surf-strong",
  storageBucket: "surf-strong.firebasestorage.app",
  messagingSenderId: "394448667638",
  appId: "1:394448667638:web:68313a18ac5ccf33643dd7"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

console.log('🔥 Firebase initialized');

const SurfTrainingApp = () => {
  // Onboarding state
  const [onboardingComplete, setOnboardingComplete] = useState(() => {
    return localStorage.getItem('surfstrong_onboarding_complete') === 'true';
  });
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [surfExperience, setSurfExperience] = useState(() => {
    return localStorage.getItem('surfstrong_surf_experience') || null;
  });
  const [fitnessLevel, setFitnessLevel] = useState(() => {
    return localStorage.getItem('surfstrong_fitness_level') || null;
  });
  
  // App state
  const [currentWeek, setCurrentWeek] = useState(() => {
    const saved = localStorage.getItem('surfstrong_current_week');
    return saved ? parseInt(saved) : 1;
  });
  const [selectedDay, setSelectedDay] = useState('monday');
  const [completedSets, setCompletedSets] = useState(() => {
    const saved = localStorage.getItem('surfstrong_completed_sets');
    return saved ? JSON.parse(saved) : {};
  });
  const [showScienceModal, setShowScienceModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  
  // Timer state
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Progress tracking
  const [workoutHistory, setWorkoutHistory] = useState(() => {
    const saved = localStorage.getItem('surfstrong_workout_history');
    return saved ? JSON.parse(saved) : {};
  });
  const [workoutLogs, setWorkoutLogs] = useState(() => {
    const saved = localStorage.getItem('surfstrong_workout_logs');
    return saved ? JSON.parse(saved) : {};
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [currentLogKey, setCurrentLogKey] = useState(null);
  const [tempLog, setTempLog] = useState({ weight: '', reps: '', notes: '' });
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  // Subscription state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [trialStartDate, setTrialStartDate] = useState(() => {
    const saved = localStorage.getItem('surfstrong_trial_start');
    if (saved) return saved;
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('surfstrong_trial_start', today);
    return today;
  });
  const [isSubscribed, setIsSubscribed] = useState(() => {
    return localStorage.getItem('surfstrong_subscribed') === 'true';
  });
  
  // Suggestion box state
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);
  
  // Firebase Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Cloud sync state
  const [syncStatus, setSyncStatus] = useState('local');
  const saveTimeoutRef = useRef(null);

  // Tier system
  const tiers = { GROM: { multiplier: 0.6, label: 'Grom', color: '#4ecdc4', description: 'Perfect for beginners building a foundation' }, RIPPER: { multiplier: 0.8, label: 'Ripper', color: '#f7d794', description: 'Intermediate training for progressing surfers' }, CHARGER: { multiplier: 1.0, label: 'Charger', color: '#ff6b6b', description: 'Advanced program for serious athletes' }, PRO: { multiplier: 1.2, label: 'Pro', color: '#a855f7', description: 'Elite training for competitive surfers' }};
  
  const getDifficultyTier = () => {
    const expMap = { 'beginner': 0, 'intermediate': 1, 'advanced': 2, 'expert': 3 };
    const fitMap = { 'beginner': 0, 'moderate': 1, 'athletic': 2, 'elite': 3 };
    const exp = expMap[surfExperience] || 0;
    const fit = fitMap[fitnessLevel] || 0;
    const combined = Math.floor((exp + fit) / 2);
    return ['GROM', 'RIPPER', 'CHARGER', 'PRO'][combined];
  };
  
  const tierInfo = tiers[getDifficultyTier()] || tiers.GROM;
  const tierMultiplier = tierInfo.multiplier;

  // Exercise database (abbreviated for file size - full version has 72 exercises)
  const exerciseDatabase = {
    'Pop-Up Burpees': { muscles: 'Full body explosive power', why: 'Mimics the exact movement pattern of popping up on your board', coaching: 'Start prone, explosively jump to surf stance, return to start' },
    'Indo Board Balance': { muscles: 'Core, ankles, proprioception', why: 'Develops the dynamic balance needed for wave riding', coaching: 'Start with wall support, progress to eyes closed' },
    'Paddle Swimmers': { muscles: 'Lats, shoulders, rotator cuff', why: 'Builds paddle endurance for long sessions', coaching: 'Prone position, alternate arm reaches with thumb-up grip' },
    'Turkish Get-Ups': { muscles: 'Full body stability', why: 'Builds the complex movement patterns needed for surfing', coaching: 'Go slow, maintain shoulder stability throughout' },
    'Duck Dive Push-Ups': { muscles: 'Chest, shoulders, triceps', why: 'Strengthens the pushing motion for duck dives', coaching: 'Explosive push with core engaged' },
    'Rotational Medicine Ball Throws': { muscles: 'Obliques, hips, shoulders', why: 'Develops rotational power for turns', coaching: 'Rotate from hips, release at chest height' },
    'Single-Leg Romanian Deadlifts': { muscles: 'Hamstrings, glutes, balance', why: 'Builds single-leg stability for rail-to-rail transitions', coaching: 'Hinge at hips, keep back flat' },
    'Plank to Push-Up': { muscles: 'Core, shoulders, triceps', why: 'Core stability with arm transitions', coaching: 'Keep hips level throughout' },
    'Box Jumps': { muscles: 'Quads, glutes, calves', why: 'Explosive power for aerial maneuvers', coaching: 'Land softly, step down to protect knees' },
    'Dead Hangs': { muscles: 'Grip, lats, shoulders', why: 'Grip endurance for holding rails', coaching: 'Relax shoulders, breathe steadily' },
    'Hip Flexor Stretch': { muscles: 'Hip flexors, quads', why: 'Mobility for pop-ups and turns', coaching: 'Keep torso upright, squeeze glute' },
    'Thread the Needle': { muscles: 'Thoracic spine, shoulders', why: 'Upper back mobility for paddling', coaching: 'Follow your hand with your eyes' },
    'Foam Rolling': { muscles: 'Full body myofascial release', why: 'Recovery and mobility maintenance', coaching: 'Slow rolls, pause on tender spots' },
    'Breath Work': { muscles: 'Diaphragm, nervous system', why: 'Breath control for hold-downs', coaching: 'Practice box breathing: 4-4-4-4 pattern' },
  };

  // Weekly program
  const weeklyProgram = {
    monday: { name: 'Power & Pop-Up', focus: 'Explosive movements for quick pop-ups', warmup: '5 min jump rope + arm circles', exercises: [
      { name: 'Pop-Up Burpees', baseSets: 3, baseReps: '8', rest: '60s', equipment: 'None' },
      { name: 'Box Jumps', baseSets: 3, baseReps: '8', rest: '90s', equipment: 'Box/Step' },
      { name: 'Duck Dive Push-Ups', baseSets: 3, baseReps: '10', rest: '60s', equipment: 'None' },
      { name: 'Plank to Push-Up', baseSets: 3, baseReps: '8 each arm', rest: '60s', equipment: 'None' },
    ], cooldown: '5 min stretching + deep breathing' },
    tuesday: { name: 'Paddle Endurance', focus: 'Building stamina for long paddle-outs', warmup: '5 min light cardio + shoulder circles', exercises: [
      { name: 'Paddle Swimmers', baseSets: 3, baseReps: '30s', rest: '45s', equipment: 'None' },
      { name: 'Dead Hangs', baseSets: 3, baseReps: '30s', rest: '60s', equipment: 'Pull-up bar' },
      { name: 'Single-Leg Romanian Deadlifts', baseSets: 3, baseReps: '8 each', rest: '60s', equipment: 'Dumbbell optional' },
    ], cooldown: 'Shoulder stretches + Thread the Needle' },
    wednesday: { name: 'Active Recovery', focus: 'Mobility and restoration', warmup: '5 min easy walking', exercises: [
      { name: 'Foam Rolling', baseSets: 1, baseReps: '10 min', rest: '-', equipment: 'Foam roller' },
      { name: 'Hip Flexor Stretch', baseSets: 2, baseReps: '60s each', rest: '30s', equipment: 'None' },
      { name: 'Thread the Needle', baseSets: 2, baseReps: '8 each side', rest: '30s', equipment: 'None' },
      { name: 'Breath Work', baseSets: 1, baseReps: '5 min', rest: '-', equipment: 'None' },
    ], cooldown: 'Meditation or gentle yoga' },
    thursday: { name: 'Rotational Power', focus: 'Core strength for powerful turns', warmup: '5 min dynamic stretches', exercises: [
      { name: 'Rotational Medicine Ball Throws', baseSets: 3, baseReps: '8 each side', rest: '60s', equipment: 'Medicine ball' },
      { name: 'Turkish Get-Ups', baseSets: 2, baseReps: '3 each side', rest: '90s', equipment: 'Kettlebell/Dumbbell' },
      { name: 'Single-Leg Romanian Deadlifts', baseSets: 3, baseReps: '8 each', rest: '60s', equipment: 'Dumbbell' },
    ], cooldown: 'Core stretches + hip openers' },
    friday: { name: 'Balance & Stability', focus: 'Proprioception for board control', warmup: '5 min light movement', exercises: [
      { name: 'Indo Board Balance', baseSets: 3, baseReps: '60s', rest: '60s', equipment: 'Indo/Balance board' },
      { name: 'Single-Leg Romanian Deadlifts', baseSets: 3, baseReps: '8 each', rest: '60s', equipment: 'None' },
      { name: 'Plank to Push-Up', baseSets: 3, baseReps: '8 each arm', rest: '60s', equipment: 'None' },
    ], cooldown: 'Balance cool-down + stretching' },
    saturday: { name: 'Surf Session', focus: 'Active practice day', warmup: 'Beach warm-up routine', exercises: [], note: '🌊 Get in the water! Apply your training to real waves. Focus on one skill to improve.', cooldown: 'Post-surf stretching' },
    sunday: { name: 'Rest Day', focus: 'Recovery and preparation', exercises: [], note: '😴 Complete rest. Your body grows stronger during recovery. Light walking or gentle yoga only.' }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const currentDayData = weeklyProgram[selectedDay];

  // Calculations
  const calculateSets = (base, week) => Math.round(base * tierMultiplier * (1 + (week - 1) * 0.05));
  const calculateReps = (base, week) => { if (typeof base === 'string' && base.includes('s')) return base; if (typeof base === 'string') return base; return Math.round(base * tierMultiplier * (1 + (week - 1) * 0.03)); };

  // Save to localStorage
  useEffect(() => { localStorage.setItem('surfstrong_current_week', currentWeek); }, [currentWeek]);
  useEffect(() => { localStorage.setItem('surfstrong_completed_sets', JSON.stringify(completedSets)); }, [completedSets]);
  useEffect(() => { localStorage.setItem('surfstrong_workout_history', JSON.stringify(workoutHistory)); }, [workoutHistory]);
  useEffect(() => { localStorage.setItem('surfstrong_workout_logs', JSON.stringify(workoutLogs)); }, [workoutLogs]);
  useEffect(() => { localStorage.setItem('surfstrong_onboarding_complete', onboardingComplete); }, [onboardingComplete]);
  useEffect(() => { if (surfExperience) localStorage.setItem('surfstrong_surf_experience', surfExperience); }, [surfExperience]);
  useEffect(() => { if (fitnessLevel) localStorage.setItem('surfstrong_fitness_level', fitnessLevel); }, [fitnessLevel]);
  useEffect(() => { localStorage.setItem('surfstrong_subscribed', isSubscribed ? 'true' : 'false'); }, [isSubscribed]);

  // Set completion
  const isSetCompleted = (day, exIdx, setIdx) => completedSets[`${currentWeek}-${day}-${exIdx}-${setIdx}`];
  const isExerciseCompleted = (day, exIdx, totalSets) => { for (let i = 0; i < totalSets; i++) { if (!isSetCompleted(day, exIdx, i)) return false; } return true; };
  
  const toggleSetCompletion = (day, exIdx, setIdx, exercise) => {
    const key = `${currentWeek}-${day}-${exIdx}-${setIdx}`;
    const newCompleted = { ...completedSets, [key]: !completedSets[key] };
    setCompletedSets(newCompleted);
    
    if (!completedSets[key]) {
      const restSeconds = parseInt(exercise.rest) || 60;
      if (restSeconds > 0) startTimer(restSeconds);
    }
    
    const dateKey = new Date().toISOString().split('T')[0];
    const historyKey = `${dateKey}-${day}`;
    if (!workoutHistory[historyKey]) {
      setWorkoutHistory(prev => ({ ...prev, [historyKey]: { date: dateKey, day, week: currentWeek, setsCompleted: 0 } }));
    }
    setWorkoutHistory(prev => ({ ...prev, [historyKey]: { ...prev[historyKey], setsCompleted: (prev[historyKey]?.setsCompleted || 0) + (completedSets[key] ? -1 : 1) } }));
  };

  // Timer
  const playBeep = (frequency = 800, duration = 200) => {
    try {
      if (!audioContextRef.current) audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration / 1000);
    } catch (e) { console.log('Audio not available'); }
  };
  
  const startTimer = (seconds) => { setActiveTimer(seconds); setTimerRunning(true); };
  const stopTimer = () => { setActiveTimer(null); setTimerRunning(false); if (timerRef.current) clearInterval(timerRef.current); };
  
  useEffect(() => {
    if (timerRunning && activeTimer !== null) {
      timerRef.current = setInterval(() => {
        setActiveTimer(prev => {
          if (prev <= 1) { playBeep(1000, 500); setTimeout(() => playBeep(1000, 500), 600); setTimeout(() => playBeep(1200, 800), 1200); setTimerRunning(false); return null; }
          if (prev <= 4) playBeep(600, 100);
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timerRunning]);

  // Progress calculations
  const getDayProgress = (day) => { const dayData = weeklyProgram[day]; let total = 0, completed = 0; dayData.exercises.forEach((ex, exIdx) => { const sets = calculateSets(ex.baseSets, currentWeek); total += sets; for (let i = 0; i < sets; i++) { if (isSetCompleted(day, exIdx, i)) completed++; } }); return { total, completed }; };
  const getWeekProgress = () => { let total = 0, completed = 0; days.forEach(day => { const p = getDayProgress(day); total += p.total; completed += p.completed; }); return total > 0 ? Math.round((completed / total) * 100) : 0; };

  // Stats
  const getTodayString = () => new Date().toISOString().split('T')[0];
  const calculateStreak = () => { let streak = 0; let date = new Date(); for (let i = 0; i < 365; i++) { const dateStr = date.toISOString().split('T')[0]; const hasWorkout = Object.keys(workoutHistory).some(key => key.startsWith(dateStr) && workoutHistory[key].setsCompleted > 0); if (hasWorkout) { streak++; date.setDate(date.getDate() - 1); } else if (i > 0) break; else date.setDate(date.getDate() - 1); } return streak; };
  const streak = calculateStreak();
  const totalWorkouts = Object.values(workoutHistory).filter(w => w.setsCompleted > 0).length;
  const totalSetsCompleted = Object.values(workoutHistory).reduce((sum, w) => sum + (w.setsCompleted || 0), 0);

  // Log modal
  const openLogModal = (exIdx, setIdx, exercise) => { const key = `${currentWeek}-${selectedDay}-${exIdx}-${setIdx}`; setCurrentLogKey(key); setTempLog(workoutLogs[key] || { weight: '', reps: '', notes: '' }); setShowLogModal(true); };
  const saveLog = () => { if (currentLogKey) { setWorkoutLogs(prev => ({ ...prev, [currentLogKey]: tempLog })); } setShowLogModal(false); };
  const openScienceModal = (name) => { setSelectedExercise(name); setShowScienceModal(true); };

  // Trial
  const getTrialDaysLeft = () => { const start = new Date(trialStartDate); const now = new Date(); const diff = Math.ceil((now - start) / (1000 * 60 * 60 * 24)); return Math.max(0, 5 - diff); };
  const trialDaysLeft = getTrialDaysLeft();
  
  const handleSubscribe = () => { setIsSubscribed(true); localStorage.setItem('surfstrong_subscribed', 'true'); };
  const handleCancelSubscription = () => { if (window.confirm('Are you sure you want to cancel?')) { setIsSubscribed(false); localStorage.setItem('surfstrong_subscribed', 'false'); }};

  // Suggestion
  const handleSubmitSuggestion = () => { if (suggestion.trim()) { const existing = JSON.parse(localStorage.getItem('surfstrong_suggestions') || '[]'); existing.push({ text: suggestion, date: new Date().toISOString(), tier: getDifficultyTier(), userId: user?.uid || 'anonymous' }); localStorage.setItem('surfstrong_suggestions', JSON.stringify(existing)); setSuggestionSubmitted(true); setSuggestion(''); setTimeout(() => { setSuggestionSubmitted(false); setShowSuggestionModal(false); }, 2000); }};

  // ============================================================================
  // FIREBASE AUTHENTICATION & CLOUD SYNC
  // ============================================================================
  
  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser?.email || 'signed out');
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadFromCloud(firebaseUser.uid);
      } else {
        setUser(null);
        setSyncStatus('local');
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);
  
  // Load user data from Firestore
  const loadFromCloud = async (uid) => {
    try {
      console.log('Loading data from cloud for:', uid);
      setSyncStatus('syncing');
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('Cloud data found:', data);
        if (data.onboardingComplete !== undefined) { setOnboardingComplete(data.onboardingComplete); localStorage.setItem('surfstrong_onboarding_complete', data.onboardingComplete); }
        if (data.surfExperience) { setSurfExperience(data.surfExperience); localStorage.setItem('surfstrong_surf_experience', data.surfExperience); }
        if (data.fitnessLevel) { setFitnessLevel(data.fitnessLevel); localStorage.setItem('surfstrong_fitness_level', data.fitnessLevel); }
        if (data.currentWeek) { setCurrentWeek(data.currentWeek); localStorage.setItem('surfstrong_current_week', data.currentWeek); }
        if (data.completedSets) { setCompletedSets(data.completedSets); localStorage.setItem('surfstrong_completed_sets', JSON.stringify(data.completedSets)); }
        if (data.workoutHistory) { setWorkoutHistory(data.workoutHistory); localStorage.setItem('surfstrong_workout_history', JSON.stringify(data.workoutHistory)); }
        if (data.workoutLogs) { setWorkoutLogs(data.workoutLogs); localStorage.setItem('surfstrong_workout_logs', JSON.stringify(data.workoutLogs)); }
        if (data.isSubscribed !== undefined) { setIsSubscribed(data.isSubscribed); localStorage.setItem('surfstrong_subscribed', data.isSubscribed ? 'true' : 'false'); }
      } else {
        console.log('No cloud data found, will create on first save');
      }
      setSyncStatus('synced');
    } catch (error) {
      console.error('Load from cloud error:', error);
      setSyncStatus('error');
    }
  };
  
  // Save user data to Firestore
  const saveToCloud = async () => {
    if (!user) return;
    try {
      console.log('Saving data to cloud...');
      setSyncStatus('syncing');
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        onboardingComplete,
        surfExperience,
        fitnessLevel,
        currentWeek,
        completedSets,
        workoutHistory,
        workoutLogs,
        isSubscribed,
        lastUpdated: new Date().toISOString()
      }, { merge: true });
      console.log('✅ Data synced to cloud');
      setSyncStatus('synced');
    } catch (error) {
      console.error('Save to cloud error:', error);
      setSyncStatus('error');
    }
  };
  
  // Auto-save to cloud when data changes (debounced)
  useEffect(() => {
    if (user) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => saveToCloud(), 2000);
    }
  }, [onboardingComplete, surfExperience, fitnessLevel, currentWeek, completedSets, workoutHistory, workoutLogs, isSubscribed, user]);
  
  // Auth handlers
  const handleLogin = async () => {
    setAuthError('');
    try {
      console.log('Attempting login for:', authEmail);
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      console.log('✅ Login successful');
      setShowAuthModal(false);
      setAuthEmail('');
      setAuthPassword('');
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      if (error.code === 'auth/user-not-found') setAuthError('No account found with this email.');
      else if (error.code === 'auth/wrong-password') setAuthError('Incorrect password.');
      else if (error.code === 'auth/invalid-email') setAuthError('Invalid email address.');
      else if (error.code === 'auth/invalid-credential') setAuthError('Invalid email or password.');
      else setAuthError(error.message);
    }
  };
  
  const handleSignup = async () => {
    setAuthError('');
    try {
      console.log('Attempting signup for:', authEmail);
      await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      console.log('✅ Signup successful');
      setShowAuthModal(false);
      setAuthEmail('');
      setAuthPassword('');
    } catch (error) {
      console.error('Signup error:', error.code, error.message);
      if (error.code === 'auth/email-already-in-use') setAuthError('An account already exists with this email.');
      else if (error.code === 'auth/weak-password') setAuthError('Password should be at least 6 characters.');
      else setAuthError(error.message);
    }
  };
  
  const handleGoogleLogin = async () => {
    try {
      console.log('Attempting Google login...');
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      console.log('✅ Google login successful');
      setShowAuthModal(false);
    } catch (error) {
      console.error('Google login error:', error);
      setAuthError(error.message);
    }
  };
  
  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setSyncStatus('local');
    setShowSettings(false);
  };

  // Loading screen
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a1628, #1a3a5c, #0d2137)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', color: '#e8f4f8' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🏄</div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  // Onboarding
  if (!onboardingComplete) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a1628, #1a3a5c, #0d2137)', padding: '20px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', color: '#e8f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🏄</div>
          <h1 style={{ fontSize: '1.8rem', marginBottom: '8px', background: 'linear-gradient(135deg, #4ecdc4, #87ceeb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SURF STRONG</h1>
          <p style={{ color: '#87ceeb', marginBottom: '32px' }}>Let's personalize your training</p>
          
          {onboardingStep === 1 && (
            <div>
              <h2 style={{ fontSize: '1.2rem', marginBottom: '20px' }}>What's your surfing experience?</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[{ id: 'beginner', label: '🌊 Beginner', desc: 'Just starting out' }, { id: 'intermediate', label: '🏄 Intermediate', desc: '1-3 years surfing' }, { id: 'advanced', label: '🔥 Advanced', desc: '3+ years, comfortable in most conditions' }, { id: 'expert', label: '⚡ Expert', desc: 'Competitive or professional level' }].map(opt => (
                  <button key={opt.id} onClick={() => { setSurfExperience(opt.id); setOnboardingStep(2); }} style={{ padding: '16px', borderRadius: '12px', border: surfExperience === opt.id ? '2px solid #4ecdc4' : '1px solid rgba(255,255,255,0.1)', background: surfExperience === opt.id ? 'rgba(78,205,196,0.2)' : 'rgba(255,255,255,0.05)', color: '#e8f4f8', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ fontWeight: 600 }}>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#87ceeb' }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {onboardingStep === 2 && (
            <div>
              <h2 style={{ fontSize: '1.2rem', marginBottom: '20px' }}>What's your fitness level?</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[{ id: 'beginner', label: '🌱 Beginner', desc: 'New to fitness training' }, { id: 'moderate', label: '💪 Moderate', desc: 'Exercise 1-2x per week' }, { id: 'athletic', label: '🏃 Athletic', desc: 'Exercise 3-5x per week' }, { id: 'elite', label: '🏆 Elite', desc: 'Train daily, high performance' }].map(opt => (
                  <button key={opt.id} onClick={() => { setFitnessLevel(opt.id); setOnboardingStep(3); }} style={{ padding: '16px', borderRadius: '12px', border: fitnessLevel === opt.id ? '2px solid #4ecdc4' : '1px solid rgba(255,255,255,0.1)', background: fitnessLevel === opt.id ? 'rgba(78,205,196,0.2)' : 'rgba(255,255,255,0.05)', color: '#e8f4f8', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ fontWeight: 600 }}>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#87ceeb' }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
              <button onClick={() => setOnboardingStep(1)} style={{ marginTop: '16px', background: 'transparent', border: 'none', color: '#87ceeb', cursor: 'pointer' }}>← Back</button>
            </div>
          )}
          
          {onboardingStep === 3 && (
            <div>
              <div style={{ background: `linear-gradient(135deg, ${tierInfo.color}22, ${tierInfo.color}11)`, borderRadius: '16px', padding: '24px', marginBottom: '24px', border: `1px solid ${tierInfo.color}44` }}>
                <div style={{ fontSize: '0.8rem', color: '#87ceeb', marginBottom: '8px' }}>YOUR TRAINING LEVEL</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: tierInfo.color, marginBottom: '8px' }}>{tierInfo.label}</div>
                <div style={{ fontSize: '0.9rem', color: '#a8d5e2' }}>{tierInfo.description}</div>
              </div>
              <button onClick={() => setOnboardingComplete(true)} style={{ width: '100%', padding: '16px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', color: '#0a1628', fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer' }}>Start Training 🏄</button>
              <button onClick={() => setOnboardingStep(2)} style={{ marginTop: '12px', background: 'transparent', border: 'none', color: '#87ceeb', cursor: 'pointer' }}>← Back</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a1628, #1a3a5c, #0d2137)', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', color: '#e8f4f8', position: 'relative', overflow: 'hidden' }}>
      
      {/* Timer Overlay */}
      {activeTimer !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '6rem', fontWeight: 700, color: activeTimer <= 3 ? '#ff6b6b' : '#4ecdc4', marginBottom: '16px' }}>{activeTimer}</div>
            <div style={{ fontSize: '1.2rem', color: '#87ceeb', marginBottom: '24px' }}>Rest Timer</div>
            <button onClick={stopTimer} style={{ padding: '12px 32px', borderRadius: '12px', border: 'none', background: 'rgba(255,255,255,0.1)', color: '#e8f4f8', cursor: 'pointer' }}>Skip</button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }} onClick={() => setShowAuthModal(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '20px', padding: '24px', maxWidth: '400px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{authMode === 'login' ? '👋 Welcome Back' : '🏄 Create Account'}</h3>
              <button onClick={() => setShowAuthModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#87ceeb', fontSize: '1.2rem', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' }}>✕</button>
            </div>
            
            {authError && (
              <div style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '10px', padding: '12px', marginBottom: '16px', color: '#ff6b6b', fontSize: '0.85rem' }}>{authError}</div>
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#87ceeb', marginBottom: '6px' }}>Email</label>
              <input type="email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} placeholder="you@example.com" style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e8f4f8', fontSize: '1rem', boxSizing: 'border-box' }} />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#87ceeb', marginBottom: '6px' }}>Password</label>
              <input type="password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} placeholder="••••••••" style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e8f4f8', fontSize: '1rem', boxSizing: 'border-box' }} />
            </div>
            
            <button onClick={authMode === 'login' ? handleLogin : handleSignup} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', color: '#0a1628', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', marginBottom: '12px' }}>
              {authMode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
            
            <button onClick={handleGoogleLogin} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#e8f4f8', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
              <span>🔵</span> Continue with Google
            </button>
            
            <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#87ceeb' }}>
              {authMode === 'login' ? (
                <>Don't have an account? <button onClick={() => { setAuthMode('signup'); setAuthError(''); }} style={{ background: 'none', border: 'none', color: '#4ecdc4', cursor: 'pointer', textDecoration: 'underline' }}>Sign up</button></>
              ) : (
                <>Already have an account? <button onClick={() => { setAuthMode('login'); setAuthError(''); }} style={{ background: 'none', border: 'none', color: '#4ecdc4', cursor: 'pointer', textDecoration: 'underline' }}>Sign in</button></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowStatsModal(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '24px 24px 0 0', padding: '24px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>📊 Your Stats</h3>
              <button onClick={() => setShowStatsModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#87ceeb', fontSize: '1.2rem', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div style={{ background: 'rgba(255,107,107,0.1)', borderRadius: '16px', padding: '16px', textAlign: 'center', border: '1px solid rgba(255,107,107,0.2)' }}>
                <div style={{ fontSize: '2.5rem' }}>🔥</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#ff6b6b' }}>{streak}</div>
                <div style={{ fontSize: '0.75rem', color: '#87ceeb' }}>Day Streak</div>
              </div>
              <div style={{ background: 'rgba(78,205,196,0.1)', borderRadius: '16px', padding: '16px', textAlign: 'center', border: '1px solid rgba(78,205,196,0.2)' }}>
                <div style={{ fontSize: '2.5rem' }}>💪</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#4ecdc4' }}>{totalWorkouts}</div>
                <div style={{ fontSize: '0.75rem', color: '#87ceeb' }}>Workouts</div>
              </div>
              <div style={{ background: 'rgba(247,215,148,0.1)', borderRadius: '16px', padding: '16px', textAlign: 'center', border: '1px solid rgba(247,215,148,0.2)' }}>
                <div style={{ fontSize: '2.5rem' }}>✅</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f7d794' }}>{totalSetsCompleted}</div>
                <div style={{ fontSize: '0.75rem', color: '#87ceeb' }}>Sets Done</div>
              </div>
              <div style={{ background: 'rgba(135,206,235,0.1)', borderRadius: '16px', padding: '16px', textAlign: 'center', border: '1px solid rgba(135,206,235,0.2)' }}>
                <div style={{ fontSize: '2.5rem' }}>📅</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#87ceeb' }}>{currentWeek}</div>
                <div style={{ fontSize: '0.75rem', color: '#87ceeb' }}>Current Week</div>
              </div>
            </div>
            {/* Sync Status */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: syncStatus === 'synced' ? '#4ecdc4' : syncStatus === 'syncing' ? '#f7d794' : syncStatus === 'error' ? '#ff6b6b' : '#87ceeb' }}>
                {syncStatus === 'synced' && '☁️ Synced to cloud'}
                {syncStatus === 'syncing' && '🔄 Syncing...'}
                {syncStatus === 'error' && '⚠️ Sync error'}
                {syncStatus === 'local' && '📱 Local only - Sign in to sync'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowSettings(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '24px 24px 0 0', padding: '24px', maxWidth: '450px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Settings</h3>
              <button onClick={() => setShowSettings(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#87ceeb', fontSize: '1.2rem', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' }}>✕</button>
            </div>
            
            {/* Account Section */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '0.7rem', color: '#87ceeb', marginBottom: '8px', textTransform: 'uppercase' }}>Account</div>
              {user ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: '#0a1628', fontWeight: 700 }}>
                      {user.email?.[0]?.toUpperCase() || '👤'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{user.email}</div>
                      <div style={{ fontSize: '0.7rem', color: syncStatus === 'synced' ? '#4ecdc4' : syncStatus === 'syncing' ? '#f7d794' : '#ff6b6b' }}>
                        {syncStatus === 'synced' && '☁️ Synced'}
                        {syncStatus === 'syncing' && '🔄 Syncing...'}
                        {syncStatus === 'error' && '⚠️ Sync error'}
                        {syncStatus === 'local' && '📱 Local only'}
                      </div>
                    </div>
                  </div>
                  <button onClick={handleLogout} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,107,107,0.3)', background: 'transparent', color: '#ff6b6b', fontSize: '0.85rem', cursor: 'pointer' }}>Sign Out</button>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#a8d5e2', marginBottom: '12px' }}>Sign in to sync across devices</div>
                  <button onClick={() => { setShowSettings(false); setShowAuthModal(true); }} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', color: '#0a1628', fontWeight: 600, cursor: 'pointer' }}>Sign In / Create Account</button>
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '0.8rem', color: '#87ceeb', marginBottom: '8px' }}>Current Level: <strong style={{ color: tierInfo.color }}>{tierInfo.label}</strong></div>
              <div style={{ fontSize: '0.75rem', color: '#a8d5e2' }}>{tierInfo.description}</div>
            </div>
            
            <button onClick={() => { setOnboardingComplete(false); setOnboardingStep(1); setShowSettings(false); }} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,107,107,0.3)', background: 'rgba(255,107,107,0.1)', color: '#ff6b6b', fontWeight: 600, cursor: 'pointer', marginBottom: '12px' }}>Change My Level</button>
            
            <button onClick={() => { setShowSettings(false); setShowSubscriptionModal(true); }} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(78,205,196,0.3)', background: 'rgba(78,205,196,0.1)', color: '#4ecdc4', fontWeight: 600, cursor: 'pointer', marginBottom: '12px' }}>
              {isSubscribed ? 'Manage Subscription' : `Subscribe • ${trialDaysLeft} days left`}
            </button>
            
            <button onClick={() => { setShowSettings(false); setShowSuggestionModal(true); }} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(135,206,235,0.3)', background: 'rgba(135,206,235,0.1)', color: '#87ceeb', fontWeight: 600, cursor: 'pointer' }}>
              💡 Suggest a Feature
            </button>
          </div>
        </div>
      )}

      {/* Science Modal */}
      {showScienceModal && selectedExercise && exerciseDatabase[selectedExercise] && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowScienceModal(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '24px 24px 0 0', padding: '24px', maxWidth: '450px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{selectedExercise}</h3>
              <button onClick={() => setShowScienceModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#87ceeb', fontSize: '1.2rem', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' }}>✕</button>
            </div>
            <div style={{ background: 'rgba(78,205,196,0.1)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '0.7rem', color: '#4ecdc4', marginBottom: '4px' }}>MUSCLES WORKED</div>
              <div style={{ fontSize: '0.9rem' }}>{exerciseDatabase[selectedExercise].muscles}</div>
            </div>
            <div style={{ background: 'rgba(247,215,148,0.1)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '0.7rem', color: '#f7d794', marginBottom: '4px' }}>WHY THIS HELPS SURFING</div>
              <div style={{ fontSize: '0.9rem' }}>{exerciseDatabase[selectedExercise].why}</div>
            </div>
            <div style={{ background: 'rgba(135,206,235,0.1)', borderRadius: '12px', padding: '14px' }}>
              <div style={{ fontSize: '0.7rem', color: '#87ceeb', marginBottom: '4px' }}>COACHING TIPS</div>
              <div style={{ fontSize: '0.9rem' }}>{exerciseDatabase[selectedExercise].coaching}</div>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      {showLogModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }} onClick={() => setShowLogModal(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '20px', padding: '24px', maxWidth: '350px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem' }}>📝 Log Set</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#87ceeb', marginBottom: '4px' }}>Weight (lbs/kg)</label>
              <input type="number" value={tempLog.weight} onChange={e => setTempLog({...tempLog, weight: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e8f4f8', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#87ceeb', marginBottom: '4px' }}>Actual Reps</label>
              <input type="number" value={tempLog.reps} onChange={e => setTempLog({...tempLog, reps: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e8f4f8', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#87ceeb', marginBottom: '4px' }}>Notes</label>
              <textarea value={tempLog.notes} onChange={e => setTempLog({...tempLog, notes: e.target.value})} rows={2} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e8f4f8', resize: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowLogModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#87ceeb', cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveLog} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', color: '#0a1628', fontWeight: 600, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowSubscriptionModal(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '24px 24px 0 0', padding: '24px', maxWidth: '450px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>🏄 Surf Strong Pro</h3>
              <button onClick={() => setShowSubscriptionModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#87ceeb', fontSize: '1.2rem', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' }}>✕</button>
            </div>
            
            {isSubscribed ? (
              <div>
                <div style={{ background: 'rgba(78,205,196,0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid rgba(78,205,196,0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '1.5rem' }}>✨</span>
                    <span style={{ fontWeight: 600, color: '#4ecdc4' }}>You're a Pro!</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#a8d5e2' }}>$4.99/month • Active</div>
                </div>
                <button onClick={handleCancelSubscription} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,107,107,0.3)', background: 'rgba(255,107,107,0.1)', color: '#ff6b6b', fontWeight: 600, cursor: 'pointer' }}>
                  Cancel Subscription
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  {['12-week progressive program', '72 surf-specific exercises', 'Rest timers with audio', 'Progress tracking & streaks', 'Weight & rep logging', 'Cloud sync across devices'].map((feature, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: i < 5 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                      <span style={{ color: '#4ecdc4' }}>✓</span>
                      <span style={{ fontSize: '0.9rem' }}>{feature}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'rgba(78,205,196,0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#4ecdc4' }}>$4.99<span style={{ fontSize: '1rem', fontWeight: 400, color: '#87ceeb' }}>/month</span></div>
                  <div style={{ fontSize: '0.8rem', color: '#a8d5e2' }}>Cancel anytime</div>
                </div>
                <button onClick={handleSubscribe} style={{ width: '100%', padding: '16px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', color: '#0a1628', fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer' }}>
                  Subscribe Now
                </button>
                <p style={{ fontSize: '0.7rem', color: '#6b8a94', textAlign: 'center', marginTop: '12px' }}>
                  {trialDaysLeft > 0 ? `${trialDaysLeft} days left in free trial` : 'Your free trial has ended'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suggestion Modal */}
      {showSuggestionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowSuggestionModal(false)}>
          <div style={{ background: 'linear-gradient(180deg, #1a3a5c, #0a1628)', borderRadius: '24px 24px 0 0', padding: '24px', maxWidth: '450px', width: '100%' }} onClick={e => e.stopPropagation()}>
            {suggestionSubmitted ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🤙</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#4ecdc4' }}>Thanks!</h3>
                <p style={{ color: '#87ceeb' }}>We appreciate your feedback.</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem' }}>💡 Suggestion Box</h3>
                  <button onClick={() => setShowSuggestionModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#87ceeb', fontSize: '1.2rem', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' }}>✕</button>
                </div>
                <textarea value={suggestion} onChange={e => setSuggestion(e.target.value)} placeholder="What features would you like to see?" rows={4} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e8f4f8', fontSize: '0.95rem', resize: 'none', boxSizing: 'border-box', marginBottom: '16px' }} />
                <button onClick={handleSubmitSuggestion} disabled={!suggestion.trim()} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: suggestion.trim() ? 'linear-gradient(135deg, #4ecdc4, #44a08d)' : 'rgba(255,255,255,0.1)', color: suggestion.trim() ? '#0a1628' : 'rgba(255,255,255,0.3)', fontWeight: 600, cursor: suggestion.trim() ? 'pointer' : 'not-allowed' }}>
                  Submit
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ position: 'relative', zIndex: 1, padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
        
        {/* Header */}
        <header style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, background: 'linear-gradient(135deg, #4ecdc4, #87ceeb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>SURF STRONG</h1>
              <p style={{ fontSize: '0.7rem', color: '#87ceeb', margin: 0 }}>12-Week Progressive Training</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {streak > 0 && (
                <div style={{ background: 'rgba(255,107,107,0.2)', borderRadius: '10px', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>🔥</span>
                  <span style={{ fontWeight: 700, color: '#ff6b6b' }}>{streak}</span>
                </div>
              )}
              <button onClick={() => setShowStatsModal(true)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '10px', padding: '8px', color: '#87ceeb', cursor: 'pointer' }}>📊</button>
              <button onClick={() => user ? setShowSettings(true) : setShowAuthModal(true)} style={{ background: user ? 'rgba(78,205,196,0.2)' : 'rgba(255,255,255,0.1)', border: user ? '1px solid rgba(78,205,196,0.3)' : 'none', borderRadius: '10px', padding: '8px', color: user ? '#4ecdc4' : '#87ceeb', cursor: 'pointer' }}>{user ? '☁️' : '👤'}</button>
              <button onClick={() => setShowSettings(true)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '10px', padding: '8px', color: '#87ceeb', cursor: 'pointer' }}>⚙️</button>
            </div>
          </div>
        </header>

        {/* Trial Banner */}
        {!isSubscribed && trialDaysLeft > 0 && (
          <div onClick={() => setShowSubscriptionModal(true)} style={{ background: trialDaysLeft <= 2 ? 'rgba(255,107,107,0.15)' : 'rgba(247,215,148,0.15)', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', border: trialDaysLeft <= 2 ? '1px solid rgba(255,107,107,0.3)' : '1px solid rgba(247,215,148,0.3)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, color: trialDaysLeft <= 2 ? '#ff6b6b' : '#f7d794' }}>{trialDaysLeft <= 2 ? '⚠️' : '⏳'} {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left in trial</div>
              <div style={{ fontSize: '0.8rem', color: '#87ceeb' }}>Tap to subscribe • $4.99/month</div>
            </div>
            <span>→</span>
          </div>
        )}

        {/* Week Progress */}
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '16px', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#87ceeb' }}>Week {currentWeek} of 12</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#4ecdc4' }}>{getWeekProgress()}% Complete</div>
            </div>
            <div style={{ background: `linear-gradient(135deg, ${tierInfo.color}33, ${tierInfo.color}11)`, borderRadius: '10px', padding: '6px 12px', border: `1px solid ${tierInfo.color}44` }}>
              <div style={{ fontSize: '0.65rem', color: '#87ceeb' }}>Level</div>
              <div style={{ fontWeight: 700, color: tierInfo.color }}>{tierInfo.label}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(w => (
              <button key={w} onClick={() => setCurrentWeek(w)} style={{ width: '40px', height: '32px', borderRadius: '8px', border: 'none', background: currentWeek === w ? 'linear-gradient(135deg, #4ecdc4, #44a08d)' : w < currentWeek ? 'rgba(78,205,196,0.3)' : 'rgba(255,255,255,0.1)', color: currentWeek === w ? '#0a1628' : '#e8f4f8', fontWeight: currentWeek === w ? 700 : 400, cursor: 'pointer', fontSize: '0.8rem' }}>{w}</button>
            ))}
          </div>
        </div>

        {/* Day Selector */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
          {days.map((day, i) => {
            const p = getDayProgress(day);
            const complete = p.total > 0 && p.completed === p.total;
            return (
              <button key={day} onClick={() => setSelectedDay(day)} style={{ flex: '1 0 auto', minWidth: '50px', padding: '10px 6px', borderRadius: '10px', border: 'none', background: selectedDay === day ? 'linear-gradient(135deg, #4ecdc4, #44a08d)' : complete ? 'rgba(78,205,196,0.2)' : 'rgba(255,255,255,0.05)', color: selectedDay === day ? '#0a1628' : '#e8f4f8', cursor: 'pointer', position: 'relative' }}>
                <div style={{ fontWeight: 700, fontSize: '0.75rem' }}>{dayLabels[i]}</div>
                <div style={{ fontSize: '0.6rem', opacity: 0.8 }}>{weeklyProgram[day].name.split(' ')[0]}</div>
                {complete && <span style={{ position: 'absolute', top: 2, right: 4, fontSize: '0.6rem' }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Day Content */}
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(78,205,196,0.2), rgba(68,160,141,0.2))', padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '1.2rem' }}>{currentDayData.name}</h2>
            <p style={{ margin: 0, color: '#87ceeb', fontSize: '0.85rem' }}>{currentDayData.focus}</p>
          </div>

          <div style={{ padding: '16px' }}>
            {currentDayData.warmup && (
              <div style={{ background: 'rgba(247,215,148,0.1)', borderRadius: '10px', padding: '12px', marginBottom: '12px', border: '1px solid rgba(247,215,148,0.2)' }}>
                <div style={{ fontSize: '0.7rem', color: '#f7d794', marginBottom: '4px' }}>🔥 WARM-UP</div>
                <div style={{ fontSize: '0.9rem' }}>{currentDayData.warmup}</div>
              </div>
            )}

            {currentDayData.note && (
              <div style={{ background: 'rgba(135,206,235,0.1)', borderRadius: '10px', padding: '12px', marginBottom: '12px', border: '1px solid rgba(135,206,235,0.2)' }}>
                <div style={{ fontSize: '0.9rem' }}>{currentDayData.note}</div>
              </div>
            )}

            {currentDayData.exercises.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🌊</div>
                <p style={{ color: '#87ceeb' }}>No exercises today - enjoy your rest!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {currentDayData.exercises.map((ex, exIdx) => {
                  const sets = calculateSets(ex.baseSets, currentWeek);
                  const reps = calculateReps(ex.baseReps, currentWeek);
                  const done = isExerciseCompleted(selectedDay, exIdx, sets);
                  return (
                    <div key={exIdx} style={{ background: done ? 'rgba(78,205,196,0.1)' : 'rgba(255,255,255,0.03)', borderRadius: '14px', padding: '14px', border: done ? '1px solid rgba(78,205,196,0.3)' : '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div>
                          <h3 style={{ margin: '0 0 2px 0', fontSize: '1rem', color: done ? '#4ecdc4' : '#e8f4f8' }}>{ex.name} {done && '✓'}</h3>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#87ceeb' }}>{ex.equipment}</p>
                        </div>
                        <button onClick={() => openScienceModal(ex.name)} style={{ background: 'rgba(135,206,235,0.2)', border: 'none', borderRadius: '8px', padding: '6px 10px', color: '#87ceeb', cursor: 'pointer', fontSize: '0.75rem' }}>🧬 Why</button>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '10px', marginBottom: '10px', justifyContent: 'space-around' }}>
                        <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#4ecdc4' }}>{sets}</div><div style={{ fontSize: '0.65rem', color: '#87ceeb' }}>SETS</div></div>
                        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                        <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f7d794' }}>{reps}</div><div style={{ fontSize: '0.65rem', color: '#87ceeb' }}>REPS</div></div>
                        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                        <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{ex.rest}</div><div style={{ fontSize: '0.65rem', color: '#87ceeb' }}>REST</div></div>
                      </div>

                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {Array.from({ length: sets }).map((_, setIdx) => {
                          const completed = isSetCompleted(selectedDay, exIdx, setIdx);
                          const logKey = `${currentWeek}-${selectedDay}-${exIdx}-${setIdx}`;
                          const hasLog = workoutLogs[logKey]?.weight || workoutLogs[logKey]?.notes;
                          return (
                            <div key={setIdx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              <button onClick={() => toggleSetCompletion(selectedDay, exIdx, setIdx, ex)} style={{ width: '44px', height: '44px', borderRadius: '10px', border: 'none', background: completed ? 'linear-gradient(135deg, #4ecdc4, #44a08d)' : 'rgba(255,255,255,0.1)', color: completed ? '#0a1628' : '#e8f4f8', cursor: 'pointer', fontSize: '1rem', fontWeight: 600 }}>{completed ? '✓' : setIdx + 1}</button>
                              <button onClick={() => openLogModal(exIdx, setIdx, ex)} style={{ background: hasLog ? 'rgba(247,215,148,0.3)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', padding: '2px 8px', color: hasLog ? '#f7d794' : '#87ceeb', cursor: 'pointer', fontSize: '0.65rem' }}>{hasLog ? '📝' : '+'}</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {currentDayData.cooldown && (
              <div style={{ background: 'rgba(135,206,235,0.1)', borderRadius: '10px', padding: '12px', marginTop: '12px', border: '1px solid rgba(135,206,235,0.2)' }}>
                <div style={{ fontSize: '0.7rem', color: '#87ceeb', marginBottom: '4px' }}>❄️ COOL-DOWN</div>
                <div style={{ fontSize: '0.9rem' }}>{currentDayData.cooldown}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Suggestion Button */}
      <button onClick={() => setShowSuggestionModal(true)} style={{ position: 'fixed', bottom: '24px', right: '24px', width: '56px', height: '56px', borderRadius: '50%', border: 'none', background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', color: '#0a1628', fontSize: '1.5rem', cursor: 'pointer', boxShadow: '0 4px 20px rgba(78,205,196,0.4)', zIndex: 100 }}>💡</button>
    </div>
  );
};

export default SurfTrainingApp;
